import * as CalculatorStepTracker from "@omni/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@omni/services/tracking/trackers/tools/calculators/config";
import { isDefined } from "@omni/utils/helper.utils";
import { parseFloatSafe } from "@omni/utils/number.utils";
import queryString from "query-string";
import {
    createContext,
    PropsWithChildren,
    use,
    useEffect,
    useMemo,
    useState
} from "react";

import { GravelShoppingList } from "./gravelCalculator.types";

type GravelCalculationContextValue = {
    setLength: (length: string) => void;
    setWidth: (width: string) => void;
    setThickness: (thickness: string) => void;
    setWeight: (weight: string) => void;
    length: string;
    width: string;
    thickness: string;
    weight: string;
    result: GravelShoppingList;
    hasValidResult: boolean;
};

type CallToActionContextProviderProps = PropsWithChildren<{}>;

export const GravelCalculationContext = createContext<
    GravelCalculationContextValue | undefined
>(undefined);
GravelCalculationContext.displayName = "GravelCalculationContext";

export const GravelCalculationContextProvider = ({
    children
}: CallToActionContextProviderProps) => {
    const [length, setLength] = useState<string>("0");
    const [width, setWidth] = useState<string>("0");
    const [thickness, setThickness] = useState<string>("0");
    const [weight, setWeight] = useState<string>("16");

    useEffect(() => {
        const q = queryString.parse(location.search, {
            parseNumbers: true,
            parseBooleans: true
        });

        setLength(q.gLength?.toString() || "0");
        setWidth(q.width?.toString() || "0");
        setThickness(q.thickness?.toString() || "0");
        setWeight(q.weight?.toString() || "16");
    }, []);

    const result = useMemo<GravelShoppingList>(() => {
        const thicknessNr = parseFloatSafe(thickness, 0);
        const widthNr = parseFloatSafe(width, 0);
        const lengthNr = parseFloatSafe(length, 0);
        const weightNr = parseFloatSafe(weight, 0);

        const volume = thicknessNr * widthNr * lengthNr;
        const kgGravel = volume * 1500;

        return {
            volume,
            kgGravel,
            bags: {
                amount: Math.ceil(kgGravel / weightNr),
                weight: weightNr
            }
        };
    }, [length, width, thickness, weight]);

    const hasValidResult = useMemo(() => {
        return result.volume > 0;
    }, [result]);

    useEffect(() => {
        CalculatorStepTracker.trackStart(CALCULATOR_KEYS.gravel);
    }, []);

    return (
        <GravelCalculationContext
            value={{
                setLength,
                setWidth,
                setThickness,
                setWeight,
                length,
                width,
                thickness,
                weight,
                result,
                hasValidResult
            }}
        >
            {children}
        </GravelCalculationContext>
    );
};

export const useGravelCalculationContext = () => {
    const context = use<GravelCalculationContextValue | undefined>(
        GravelCalculationContext
    );

    if (!isDefined(context)) {
        throw new Error(
            `${GravelCalculationContext.displayName} is undefined.`
        );
    }

    return context;
};
